import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Link from '../../components/LanguageSwitcher/LocaleLink'
import { motion } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Reveal from '../../components/Reveal/Reveal'
import EngagementModels from '../../components/EngagementModels/EngagementModels'
import TrustStrip from '../../components/TrustStrip/TrustStrip'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import Seo, { breadcrumbJsonLd, serviceJsonLd } from '../../components/Seo/Seo'
import useEntryMotion from '../../components/motion/useEntryMotion'
import { resolveProjects } from '../../data/projects'
import ProjectMedia from '../../components/ProjectMedia/ProjectMedia'
import allServices, { SERVICE_CATEGORIES } from '../../data/services'
import { localizeServices } from '../../data/localizeService'
import ogImage from '../../images/archi/medicProductPage.jpg'
import AbstractVisual from '../../components/AbstractVisual/AbstractVisual'
import { pagePhotos, serviceCategoryPhotos } from '../../data/photos'
import ServiceSpotlight from '../../components/ServiceSpotlight/ServiceSpotlight'
import './services.scss'

// Projects shown as case studies, in this order.
const CASE_STUDIES = ['MedicMinder', 'ArchiML', 'MedLocator']

const Services = () => {
  const { translations } = useContext(LanguageContext)
  const navigate = useNavigate()
  const animateIn = useEntryMotion()
  const [activePhase, setActivePhase] = useState(0)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const caseStudies = resolveProjects(translations)
    .filter((project) => CASE_STUDIES.includes(project.title))
    .sort((a, b) => CASE_STUDIES.indexOf(a.title) - CASE_STUDIES.indexOf(b.title))

  const stages = [1, 2, 3, 4, 5, 6].map((n) => translations[`svcStage${n}`])

  const phases = [1, 2, 3, 4, 5].map((n) => ({
    title: translations[`svcPhase${n}`],
    text: translations[`svcPhase${n}Text`],
  }))

  const reasons = [1, 2, 3, 4].map((n) => ({
    number: `0${n}`,
    title: translations[`svcWhy${n}Title`],
    text: translations[`svcWhy${n}Text`],
  }))

  // Service copy lives in data/services.js in English; swap in the active
  // language where a translation exists.
  const services = localizeServices(allServices, translations)

  const categories = SERVICE_CATEGORIES.map((category) => {
    const key = `svcCat${category.id.charAt(0).toUpperCase()}${category.id.slice(1)}`
    return {
      ...category,
      name: translations[key] || category.label,
      text: translations[`${key}Text`],
      photo: serviceCategoryPhotos[category.id],
      services: services.filter((service) => service.category === category.id),
    }
  }).filter((category) => category.services.length)

  const openProject = (link) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener')
      return
    }
    navigate(link)
  }

  const hero = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 40 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  })

  return (
    <Layout>
      <Seo
        title='AI, Web & Mobile Development Services'
        description='MLMind services: custom AI and machine learning, generative AI and RAG, AI agents, computer vision, data engineering, MLOps, web and mobile app development, and UI/UX design.'
        image={ogImage}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
          ]),
          serviceJsonLd(services.map((s) => ({ title: s.name, description: s.summary }))),
        ]}
      />

      <section className='svc-hero'>
        <div className='svc-inner'>
          <nav className='svc-breadcrumb' aria-label='Breadcrumb'>
            <Link to='/'>{translations.uiHome}</Link>
            <span aria-hidden='true'>/</span>
            <span aria-current='page'>{translations.navServices}</span>
          </nav>

          <div className='svc-hero-grid'>
            <div className='svc-hero-copy'>
              <motion.p className='svc-eyebrow' {...hero(0)}>{translations.svcEyebrow}</motion.p>
              <motion.h1 className='svc-hero-title' {...hero(0.08)}>{translations.svcTitle}</motion.h1>
              <motion.p className='svc-hero-tagline' {...hero(0.16)}>{translations.svcTagline}</motion.p>
              <motion.p className='svc-hero-text' {...hero(0.24)}>{translations.svcHeroText}</motion.p>

              <motion.div className='svc-actions' {...hero(0.32)}>
                <Link to='/contact-us' className='svc-btn svc-btn-primary'>{translations.svcHeroPrimary}</Link>
                <Link to='/projects' className='svc-btn svc-btn-ghost'>
                  {translations.svcHeroSecondary} <span aria-hidden='true'>→</span>
                </Link>
              </motion.div>
            </div>
            <motion.div className='svc-hero-visual-wrap' {...hero(0.2)}>
              {pagePhotos.services ? (
                <img className='svc-hero-visual svc-hero-photo' src={pagePhotos.services} alt='' />
              ) : (
                <AbstractVisual className='svc-hero-visual' shape='ribbon' />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <section className='svc-section svc-statement'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcStatement}</h2>
          </Reveal>
          <div className='svc-statement-cols'>
            <Reveal delay={0.1}><p>{translations.svcStatement1}</p></Reveal>
            <Reveal delay={0.2}><p>{translations.svcStatement2}</p></Reveal>
          </div>
        </div>
      </section>

      <section className='svc-section' id='core-services'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcCoreTitle}</h2>
            <p className='svc-section-sub'>{translations.svcCoreSub}</p>
          </Reveal>

          {/* Category jump bar: photo, name and count, scrolls to the block */}
          <Reveal>
            <nav className='svc-cat-nav' aria-label={translations.svcCoreTitle}>
              {categories.map((category) => (
                <a
                  key={category.id}
                  href={`#svc-cat-${category.id}`}
                  className='svc-cat-chip'
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById(`svc-cat-${category.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <img src={category.photo} alt='' loading='lazy' />
                  <span className='svc-cat-chip-name'>{category.name}</span>
                  <span className='svc-cat-chip-count'>{category.services.length}</span>
                </a>
              ))}
            </nav>
          </Reveal>

          {/* One block per category: sticky photo and intro on the left,
              compact service rows on the right */}
          {categories.map((category, catIndex) => (
            <div key={category.id} id={`svc-cat-${category.id}`} className='svc-cat-block'>
              <Reveal className='svc-cat-aside'>
                <div className='svc-cat-photo'>
                  <img src={category.photo} alt='' loading='lazy' />
                  <span className='svc-cat-index'>{String(catIndex + 1).padStart(2, '0')}</span>
                </div>
                <h3 className='svc-cat-title'>{category.name}</h3>
                <p className='svc-cat-text'>{category.text}</p>
              </Reveal>
              <ul className='svc-rows'>
                {category.services.map((service, index) => (
                  <li key={service.slug}>
                    <Reveal delay={(index % 4) * 0.05}>
                      <Link to={`/services/${service.slug}`} className='svc-row'>
                        <span className='svc-row-num'>{String(services.indexOf(service) + 1).padStart(2, '0')}</span>
                        <span className='svc-row-body'>
                          <span className='svc-row-title'>{service.name}</span>
                          <span className='svc-row-text'>{service.summary}</span>
                        </span>
                        <span className='svc-row-arrow' aria-hidden='true'>→</span>
                      </Link>
                    </Reveal>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* For readers who don't know which service they need yet */}
          <div className='svc-unsure'>
            <Reveal className='svc-unsure-card'>
              <p className='svc-unsure-label'>{translations.readyKicker}</p>
              <h3>{translations.homeQuizTitle}</h3>
              <p>{translations.homeQuizSub}</p>
              <Link to='/ai-readiness' className='svc-link-more'>{translations.homeQuizCta} <span aria-hidden='true'>→</span></Link>
            </Reveal>
            <Reveal delay={0.08} className='svc-unsure-card svc-unsure-card-dark'>
              <p className='svc-unsure-label'>{translations.homeEstimateLabel}</p>
              <h3>{translations.homeEstimateTitle}</h3>
              <p>{translations.homeEstimateSub}</p>
              <Link to='/estimate' className='svc-link-more'>{translations.homeEstimateCta} <span aria-hidden='true'>→</span></Link>
            </Reveal>
          </div>
        </div>
      </section>

      <ServiceSpotlight />

      <section className='svc-section svc-work'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow'>{translations.svcWorkEyebrow}</p>
            <h2 className='svc-display'>{translations.svcWorkTitle}</h2>
          </Reveal>

          <div className='svc-work-grid'>
            {caseStudies.map((project, index) => (
              <Reveal key={project.title} delay={index * 0.1}>
                <a
                  className='svc-work-card'
                  href={project.link}
                  onClick={(e) => {
                    e.preventDefault()
                    openProject(project.link)
                  }}
                  {...(project.link.startsWith('http') ? { target: '_blank', rel: 'noopener' } : {})}
                >
                  <div className='svc-work-img-frame'>
                    <ProjectMedia project={project} loading='lazy' width='640' height='420' />
                  </div>
                  <p className='svc-work-tag'>{project.tag}</p>
                  <h3 className='svc-work-title'>
                    {project.title} <span aria-hidden='true'>→</span>
                  </h3>
                  <p className='svc-work-text'>{project.text}</p>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <Link to='/projects' className='svc-btn svc-btn-ghost svc-work-all'>{translations.svcWorkAll}</Link>
          </Reveal>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcProcessTitle}</h2>
          </Reveal>

          <Reveal delay={0.1}>
            <ol className='svc-stages' aria-label={translations.svcStagesLabel}>
              {stages.map((stage, index) => (
                <li key={stage} className='svc-stage'>
                  <span className='svc-stage-num'>{String(index + 1).padStart(2, '0')}</span>
                  <span className='svc-stage-name'>{stage}</span>
                </li>
              ))}
            </ol>
          </Reveal>

          <div className='svc-two-col'>
            <Reveal>
              <h3>{translations.svcDirectionTitle}</h3>
              <p>{translations.svcDirectionText}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h3>{translations.svcDeliverTitle}</h3>
              <p>{translations.svcDeliverText}</p>
            </Reveal>
          </div>

          {/* Every panel stays in the DOM (inactive ones are hidden) so the
              whole process is readable to search engines and the prerender. */}
          <div className='svc-phases'>
            <div className='svc-phase-list' role='tablist' aria-orientation='vertical'>
              {phases.map((phase, index) => (
                <button
                  key={phase.title}
                  type='button'
                  role='tab'
                  id={`svc-phase-tab-${index}`}
                  aria-controls={`svc-phase-panel-${index}`}
                  aria-selected={activePhase === index}
                  className={`svc-phase-tab ${activePhase === index ? 'is-active' : ''}`}
                  onClick={() => setActivePhase(index)}
                  onMouseEnter={() => setActivePhase(index)}
                >
                  <span>{phase.title}</span>
                  <span aria-hidden='true'>→</span>
                </button>
              ))}
            </div>

            <div className='svc-phase-panels'>
              {phases.map((phase, index) => (
                <div
                  key={phase.title}
                  role='tabpanel'
                  id={`svc-phase-panel-${index}`}
                  aria-labelledby={`svc-phase-tab-${index}`}
                  className={`svc-phase-panel ${activePhase === index ? 'is-active' : ''}`}
                >
                  <p className='svc-phase-num'>{String(index + 1).padStart(2, '0')} / 05</p>
                  <h3>{phase.title}</h3>
                  <p>{phase.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='svc-section svc-why'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcWhyTitle}</h2>
          </Reveal>
          <div className='svc-why-grid'>
            {reasons.map((reason, index) => (
              <Reveal key={reason.number} delay={(index % 2) * 0.1}>
                <p className='svc-why-num'>{reason.number}</p>
                <h3 className='svc-why-title'>{reason.title}</h3>
                <p className='svc-why-text'>{reason.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <EngagementModels from='services' />
          <TrustStrip className='svc-trust' />
        </div>
      </section>

      <section className='svc-section svc-cta'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow svc-eyebrow-light'>{translations.svcCtaEyebrow}</p>
            <h2 className='svc-cta-title'>{translations.svcCtaTitle}</h2>
            <p className='svc-cta-text'>{translations.svcCtaText}</p>
            <Link to='/contact-us' className='svc-btn svc-btn-light'>{translations.svcCtaButton}</Link>
          </Reveal>
        </div>
      </section>
    </Layout>
  )
}

export default Services
